import { useEffect, useRef, useState, useCallback } from "react";
import { X, CaretLeft, CaretRight, DownloadSimple } from "@phosphor-icons/react";

/**
 * Full-screen swipe gallery for a list of photo URLs.
 *
 * Props:
 *   photos: string[] — image URLs (already auth-signed)
 *   startIndex: number
 *   onClose: () => void
 *   label?: string — small caption text (e.g., "Kitchen · Move-Out")
 */
export default function PhotoLightbox({ photos, startIndex = 0, onClose, label = "",
                                        quickTagRooms = [], quickTagPaths = null,
                                        quickTagCurrent = null, onQuickTag = null }) {
  const [idx, setIdx] = useState(startIndex);
  const [tagging, setTagging] = useState(false);
  const [tagged, setTagged] = useState({}); // idx → room (client-side feedback)
  const total = photos?.length || 0;
  const touchStart = useRef({ x: 0, y: 0 });

  const goPrev = useCallback(() => setIdx(i => (i - 1 + total) % total), [total]);
  const goNext = useCallback(() => setIdx(i => (i + 1) % total), [total]);

  const doQuickTag = async (room) => {
    if (!onQuickTag || !quickTagPaths || tagging) return;
    const path = quickTagPaths[idx];
    if (!path) return;
    setTagging(true);
    try {
      const ok = await onQuickTag(path, room);
      if (ok !== false) {
        setTagged(t => ({ ...t, [idx]: room }));
        // Auto-advance so tradesman can rapid-fire tag
        if (idx < total - 1) setIdx(idx + 1);
      }
    } finally { setTagging(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  useEffect(() => {
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!photos || total === 0) return null;

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goPrev(); else goNext();
    }
  };

  const currentUrl = photos[idx];

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex flex-col select-none"
      data-testid="photo-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 text-white/90">
        <div className="text-xs font-black uppercase tracking-widest">
          {label ? <>{label} · </> : null}
          <span className="text-white/60">{idx + 1} / {total}</span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={currentUrl}
            download
            target="_blank"
            rel="noreferrer"
            data-testid="photo-lightbox-download"
            className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10"
            title="Download original"
          >
            <DownloadSimple size={18} weight="bold" />
          </a>
          <button
            onClick={onClose}
            data-testid="photo-lightbox-close"
            className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10"
          >
            <X size={22} weight="bold" />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div
        className="flex-1 relative flex items-center justify-center px-4 pb-4"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {total > 1 && (
          <button
            onClick={goPrev}
            data-testid="photo-lightbox-prev"
            className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-white/15 hover:bg-white/30 active:bg-white/40 text-white rounded-full transition shadow-lg"
            aria-label="Previous"
          >
            <CaretLeft size={26} weight="bold" />
          </button>
        )}
        <img
          key={idx}
          src={currentUrl}
          alt=""
          data-testid="photo-lightbox-image"
          className="max-h-full max-w-full object-contain shadow-2xl animate-in fade-in duration-150"
          draggable={false}
        />
        {total > 1 && (
          <button
            onClick={goNext}
            data-testid="photo-lightbox-next"
            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-white/15 hover:bg-white/30 active:bg-white/40 text-white rounded-full transition shadow-lg"
            aria-label="Next"
          >
            <CaretRight size={26} weight="bold" />
          </button>
        )}
      </div>

      {/* Quick-tag room chip bar — only when caller supplies rooms + handler */}
      {onQuickTag && quickTagRooms.length > 0 && (
        <div className="border-t-2 border-fuchsia-500/40 px-3 pt-2.5 pb-3 bg-gradient-to-b from-black to-slate-900" data-testid="photo-lightbox-quicktag">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-fuchsia-300 inline-flex items-center gap-1">
              <span className="text-fuchsia-400">⚡</span> Tap a room to tag this photo &amp; advance
            </span>
            {tagged[idx] ? (
              <span className="text-emerald-400 inline-flex items-center gap-1">✓ tagged {tagged[idx]}</span>
            ) : quickTagCurrent ? (
              <span className="text-white/50 normal-case tracking-wider text-[10px] truncate max-w-[45%]">
                now: <span className="text-white/80">{quickTagCurrent}</span>
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2 max-h-[38vh] overflow-y-auto pb-1 -mx-1 px-1">
            {quickTagRooms.map(r => {
              const active = tagged[idx] === r;
              const current = quickTagCurrent === r && !tagged[idx];
              return (
                <button key={r} onClick={() => doQuickTag(r)} disabled={tagging}
                  data-testid={`photo-lightbox-quicktag-${r}`}
                  className={`h-12 px-4 flex-shrink-0 border-2 font-black uppercase text-xs tracking-wider whitespace-nowrap disabled:opacity-40 transition-all rounded-sm shadow-[3px_3px_0px_rgba(255,255,255,0.08)] active:translate-y-0.5 active:shadow-none ${
                    active ? "bg-emerald-500 border-emerald-400 text-slate-900"
                    : current ? "bg-white/20 border-fuchsia-400 text-white"
                    : "bg-slate-800 border-white/50 text-white hover:bg-fuchsia-600 hover:border-fuchsia-400"
                  }`}>
                  {active && <span className="mr-1">✓</span>}{r}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Thumb strip */}
      {total > 1 && (
        <div className="border-t border-white/10 px-3 py-3 overflow-x-auto flex gap-2" data-testid="photo-lightbox-strip">
          {photos.map((u, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`photo-lightbox-thumb-${i}`}
              className={`h-14 w-14 flex-shrink-0 overflow-hidden border-2 transition ${
                i === idx ? "border-white" : "border-white/20 hover:border-white/60 opacity-70"
              }`}
            >
              <img src={u} alt="" className="h-full w-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}

      {/* Mobile swipe hint — hide when quick-tag chip bar is on (they overlap) */}
      {total > 1 && !onQuickTag && (
        <div className="sm:hidden absolute bottom-24 left-0 right-0 flex justify-center pointer-events-none">
          <div className="text-white/50 text-[10px] font-black uppercase tracking-widest bg-black/50 px-2 py-1">
            ← swipe or tap arrows →
          </div>
        </div>
      )}
    </div>
  );
}
